(() => {
  if (customElements.get('br-createe-cozee')) return;

  let instagramScriptPromise;

  const processInstagram = () => {
    if (window.instgrm?.Embeds) window.instgrm.Embeds.process();
  };

  const loadInstagram = () => {
    if (window.instgrm?.Embeds) {
      processInstagram();
      return Promise.resolve();
    }

    if (instagramScriptPromise) return instagramScriptPromise;

    instagramScriptPromise = new Promise((resolve, reject) => {
      const existingScript = document.querySelector('script[src="https://www.instagram.com/embed.js"]');
      const script = existingScript || document.createElement('script');

      const handleLoad = () => {
        processInstagram();
        resolve();
      };

      const handleError = () => reject(new Error('Instagram embed script failed to load.'));

      script.addEventListener('load', handleLoad, { once: true });
      script.addEventListener('error', handleError, { once: true });

      if (!existingScript) {
        script.async = true;
        script.defer = true;
        script.src = 'https://www.instagram.com/embed.js';
        document.body.append(script);
      }
    });

    return instagramScriptPromise;
  };

  class BrCreateeCozee extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;

      this.abortController = new AbortController();
      this.productCards = Array.from(this.querySelectorAll('[data-product-card]'));
      this.scenarioTabs = Array.from(this.querySelectorAll('[role="tab"][data-scenario-title]'));
      this.faqButtons = Array.from(this.querySelectorAll('[data-faq-button]'));

      this.bindProductPreview();
      this.bindScenarioTabs();
      this.bindFaq();
      this.observeInstagram();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.instagramObserver?.disconnect();
      this.abortController = null;
      this.instagramObserver = null;
    }

    bindProductPreview() {
      this.productCards.forEach((card) => {
        card.addEventListener('mouseenter', () => this.activateProduct(card), {
          signal: this.abortController.signal,
        });
        card.addEventListener('focusin', () => this.activateProduct(card), {
          signal: this.abortController.signal,
        });
      });
    }

    activateProduct(activeCard) {
      this.productCards.forEach((card) => card.classList.toggle('is-active', card === activeCard));

      const title = this.querySelector('[data-product-preview-title]');
      const description = this.querySelector('[data-product-preview-description]');

      if (title) title.textContent = activeCard.dataset.title || '';
      if (description) description.textContent = activeCard.dataset.description || '';
    }

    bindScenarioTabs() {
      this.scenarioTabs.forEach((tab) => {
        tab.addEventListener('click', () => this.activateScenario(tab), {
          signal: this.abortController.signal,
        });
        tab.addEventListener('keydown', (event) => this.handleScenarioKeydown(event), {
          signal: this.abortController.signal,
        });
      });
    }

    activateScenario(activeTab) {
      this.scenarioTabs.forEach((tab) => {
        const isActive = tab === activeTab;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', String(isActive));
        tab.tabIndex = isActive ? 0 : -1;
      });

      const panel = this.querySelector('[role="tabpanel"]');
      const title = this.querySelector('[data-scenario-title]:not([role="tab"])');
      const description = this.querySelector('[data-scenario-description]:not([role="tab"])');
      const cta = this.querySelector('[data-scenario-cta]');
      const ctaLabel = this.querySelector('[data-scenario-cta-label]:not([role="tab"])');

      if (panel) panel.setAttribute('aria-labelledby', activeTab.id);
      if (title) title.textContent = activeTab.dataset.scenarioTitle || '';
      if (description) description.textContent = activeTab.dataset.scenarioDescription || '';

      if (cta && ctaLabel) {
        const href = activeTab.dataset.scenarioHref || '';
        ctaLabel.textContent = activeTab.dataset.scenarioCtaLabel || '';
        cta.hidden = href === '';
        if (href) cta.setAttribute('href', href);
        else cta.removeAttribute('href');
      }
    }

    handleScenarioKeydown(event) {
      const currentIndex = this.scenarioTabs.indexOf(event.currentTarget);
      let nextIndex = currentIndex;

      if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
        nextIndex = (currentIndex + 1) % this.scenarioTabs.length;
      } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
        nextIndex = (currentIndex - 1 + this.scenarioTabs.length) % this.scenarioTabs.length;
      } else if (event.key === 'Home') {
        nextIndex = 0;
      } else if (event.key === 'End') {
        nextIndex = this.scenarioTabs.length - 1;
      } else {
        return;
      }

      event.preventDefault();
      this.scenarioTabs[nextIndex].focus();
      this.activateScenario(this.scenarioTabs[nextIndex]);
    }

    bindFaq() {
      this.faqButtons.forEach((button) => {
        button.addEventListener('click', () => this.toggleFaq(button), {
          signal: this.abortController.signal,
        });
      });
    }

    toggleFaq(activeButton) {
      const wasOpen = activeButton.getAttribute('aria-expanded') === 'true';

      this.faqButtons.forEach((button) => {
        const panel = this.querySelector(`#${CSS.escape(button.getAttribute('aria-controls'))}`);
        const shouldOpen = button === activeButton && !wasOpen;
        button.setAttribute('aria-expanded', String(shouldOpen));
        button.closest('.br-createe__faq-item')?.classList.toggle('is-open', shouldOpen);
        if (panel) panel.hidden = !shouldOpen;
      });
    }

    observeInstagram() {
      const region = this.querySelector('[data-instagram-region]');
      if (!region || !region.querySelector('.instagram-media')) return;

      const startInstagram = () => {
        loadInstagram().catch(() => {
          // The accessible Reel links remain available when the third-party script fails.
        });
      };

      if (!('IntersectionObserver' in window)) {
        startInstagram();
        return;
      }

      this.instagramObserver = new IntersectionObserver(
        (entries, observer) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          observer.disconnect();
          startInstagram();
        },
        { rootMargin: '320px 0px' }
      );
      this.instagramObserver.observe(region);
    }
  }

  customElements.define('br-createe-cozee', BrCreateeCozee);
})();
